import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot, Plus, Send, Square, Trash2, Camera, ImagePlus, X as XIcon, Globe, Check, Save, Power,
  PanelLeftClose, PanelLeftOpen, RefreshCw, ListChecks, Pencil, ExternalLink,
} from "lucide-react";

import {
  fetchAgentStatus, getSession, streamApprove, streamEvents, streamCompact, patchSession, cancelRun, clearSteers,
  type TranscriptItem, type SessionDetail, type ApprovalMode, type ReasoningEffort,
} from "../api/agent";
import {
  listSessions3, createSession3, deleteSession3, steerRun3, streamMessage3, fetchSessionImage,
  fetchLiveBrowser, openLiveBrowser, liveScreenshot, liveSignal, fitImage, MAX_IMAGES,
  type Transcript3Item, type LiveBrowser,
} from "../api/agent3";
import { explainError } from "../api/client";
import { Spinner } from "../components/Spinner";
import { ImageAnnotator } from "../components/ImageAnnotator";
import { TranscriptRow } from "./AgentConsole";
import { timeSince } from "../lib/format";
import { useVncPass } from "../lib/vncPass";

/* ──────────────────────────────────────────────────────────────────────
   /agent3 — the operator agent with a LIVE browser (SuperAdmin).

     · sessions (left, v3 only)
     · live browser (centre): the np92 capture Chrome over noVNC — the same
       browser the Sessions page drives. Operator clicks in it; the agent
       drives it with live_browser_* tools; Screenshot drops the view into chat.
     · chat (right): the v2 loop (steering, ask-operator, stall recovery) with
       up to 3 images per message, each markable (box / arrow / pen).

   Session plumbing (get / approve / events / cancel / compact / patch) is the
   /api/agent API; only create, list, delete, send-with-images and steer are
   /api/agent3. Plan: docs/plans/2026-10-28-agent3-live-browser.md
   ────────────────────────────────────────────────────────────────────── */

type Attachment = { id: string; dataUrl: string; w: number; h: number; source: string; marked?: boolean };
type LedgerItem = { id: string; text: string; status: string; evidence?: string };

const SUGGESTIONS = [
  "Open the live browser on a channel that failed today and show me what the e-paper page looks like.",
  "You couldn't find the e-paper — I'll open it in the browser. Take a screenshot and tell me what you see.",
  "Look at the requests the browser captured and tell me which ones load the page images.",
];

const PHASE_TONE: Record<string, string> = {
  waiting_for_login: "var(--accent)", waiting_for_navigate: "var(--primary)", capturing: "var(--accent)",
};

export function AgentConsole3() {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [items, setItems] = useState<Transcript3Item[]>([]);
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [annotating, setAnnotating] = useState<Attachment | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [streamErr, setStreamErr] = useState<string | null>(null);
  const [runStatus, setRunStatus] = useState<string>("idle");
  const [pendingCallId, setPendingCallId] = useState<string | null>(null);
  const [mode, setMode] = useState<ApprovalMode>("bypass");
  const [effort, setEffort] = useState<ReasoningEffort>("medium");
  const [historyChars, setHistoryChars] = useState(0);
  const [lastSpeed, setLastSpeed] = useState<number | null>(null);
  const [queued, setQueued] = useState<string[]>([]);
  const [turnId, setTurnId] = useState<string | null>(null);
  const [ledger, setLedger] = useState<LedgerItem[]>([]);
  const [showBrowser, setShowBrowser] = useState(true);
  const abortRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const status = useQuery({ queryKey: ["agent/status", "v3"], queryFn: () => fetchAgentStatus("v3"), refetchInterval: 60_000 });
  const sessions = useQuery({ queryKey: ["agent3/sessions"], queryFn: listSessions3 });
  const configured = status.data?.settings.configured ?? false;

  /* ── run events → view (same contract as /agent; plus agent screenshots) ── */
  const applyEvent = useCallback((ev: Record<string, unknown>, sid: string) => {
    if (sessionIdRef.current !== sid) return;
    const t = ev.type as string;
    if (t === "turn_start") { setRunStatus("running"); return; }
    if (t === "turn_paused") {
      setRunStatus(String(ev.reason) === "needs_input" ? "needs_input" : "awaiting_approval");
      if (String(ev.reason) !== "needs_input") setPendingCallId(String(ev.call_id ?? ""));
      return;
    }
    if (t === "turn_done") {
      const rs = String(ev.run_status ?? "idle"); setRunStatus(rs);
      if (rs !== "awaiting_approval") setPendingCallId(null);
      return;
    }
    if (t === "ledger") { setLedger((ev.items as LedgerItem[]) ?? []); return; }
    if (t === "steer_injected") { setQueued([]); setItems((c) => [...c, { type: "steer", text: String(ev.text ?? "") }]); return; }
    if (t === "compact_start") { setRunStatus("running"); setItems((c) => [...c, { type: "system", text: `compacting ${String(ev.messages)} messages…` }]); return; }
    if (t === "compact_done") { setHistoryChars(Number(ev.chars_after ?? 0)); return; }
    if (t === "change_record") { setItems((c) => [...c, { type: "change_record", text: String(ev.text ?? "") }]); return; }
    if (t === "stall" || t === "recovery" || t === "report_check" || t === "risk_escalated") {
      const text = t === "stall" ? `stall detected: ${String(ev.kind ?? "")}` : t === "recovery" ? `recovery: ${String(ev.action ?? "")}`
        : t === "report_check" ? (ev.flagged ? `report check: ${String(ev.reason ?? "")}` : "")
        : `${String(ev.name ?? "")} treated as ${String(ev.risk ?? "")}`;
      if (text) setItems((c) => [...c, { type: "system", text }]);
      return;
    }
    if (t === "approval_result") setPendingCallId(null);
    if (t === "assistant_done") setLastSpeed((ev.tok_s as number | null) ?? null);
    setItems((cur) => {
      const next = [...cur];
      const last = next[next.length - 1];
      if (t === "text_delta") {
        if (last && last.type === "assistant" && !last.call_id) next[next.length - 1] = { ...last, text: (last.text ?? "") + String(ev.text ?? "") };
        else next.push({ type: "assistant", text: String(ev.text ?? "") });
      } else if (t === "reasoning_delta") {
        if (last && last.type === "reasoning") next[next.length - 1] = { ...last, text: (last.text ?? "") + String(ev.text ?? ""), chars: (last.chars ?? 0) + String(ev.text ?? "").length };
        else next.push({ type: "reasoning", text: String(ev.text ?? ""), chars: String(ev.text ?? "").length });
      } else if (t === "assistant_done") {
        if (last && last.type === "assistant") next[next.length - 1] = { ...last, tok_s: (ev.tok_s as number | null) ?? null, out_tokens: Number(ev.out_tokens ?? 0), round_ms: Number(ev.round_ms ?? 0) };
      } else if (t === "tool_call") {
        next.push({ type: "tool_call", call_id: String(ev.call_id), name: String(ev.name), arguments: (ev.arguments as Record<string, unknown>) ?? {} });
      } else if (t === "approval_required") {
        next.push({ type: "approval_required", call_id: String(ev.call_id), name: String(ev.name), arguments: (ev.arguments as Record<string, unknown>) ?? {},
                    risk: String(ev.risk ?? ""), declared_risk: ev.declared_risk ? String(ev.declared_risk) : undefined,
                    why: (ev.why as string[] | undefined), always_asks: Boolean(ev.always_asks) });
      } else if (t === "approval_result") {
        next.push({ type: "approval_result", call_id: String(ev.call_id), approved: Boolean(ev.approved), text: ev.reason ? String(ev.reason) : undefined, by: ev.by ? String(ev.by) : undefined });
      } else if (t === "tool_result") {
        next.push({ type: "tool_result", call_id: String(ev.call_id), name: String(ev.name), ok: Boolean(ev.ok), result: String(ev.result ?? ""), duration_ms: Number(ev.duration_ms ?? 0) });
      } else if (t === "image") {
        next.push({ type: "image", call_id: String(ev.call_id ?? ""), name: String(ev.name ?? ""), data_url: String(ev.data_url ?? "") });
      } else if (t === "question") {
        next.push({ type: "question", question: String(ev.question ?? ""), context: ev.context ? String(ev.context) : undefined, options: (ev.options as string[] | undefined) ?? [] });
      } else if (t === "failover") {
        next.push({ type: "system", text: `failover → ${String(ev.to ?? "")}` });
      } else if (t === "error") {
        next.push({ type: "error", text: String(ev.message ?? "error") });
      }
      return next;
    });
  }, []);

  const hydrate = useCallback((s: SessionDetail) => {
    const rs = s.run_status ?? "idle";
    setRunStatus(rs);
    setPendingCallId(rs === "awaiting_approval" ? ((s.pending_call as { call_id?: string } | null)?.call_id ?? null) : null);
    const em = s.effective_approval_mode; if (em === "bypass" || em === "ask") setMode(em);
    const ee = s.effective_reasoning_effort; if (ee === "xhigh" || ee === "medium" || ee === "low") setEffort(ee);
    if (typeof s.history_chars === "number") setHistoryChars(s.history_chars);
    setQueued((s.queued_steers as string[] | undefined) ?? []);
    setTurnId((s.turn_id as string | null | undefined) ?? null);
    setLedger(((s.ledger as LedgerItem[] | undefined) ?? []));
  }, []);

  const settle = useCallback(async (sid: string): Promise<SessionDetail | null> => {
    let s: SessionDetail | null = null;
    try {
      s = await getSession(sid);
      if (sessionIdRef.current !== sid) return null;
      hydrate(s);
      if (!s.running) setItems(s.transcript as unknown as Transcript3Item[]);
    } catch { /* ignore */ }
    void qc.invalidateQueries({ queryKey: ["agent3/sessions"] });
    return s;
  }, [qc, hydrate]);

  const attach = useCallback(async (sid: string) => {
    if (abortRef.current) return;
    const ac = new AbortController(); abortRef.current = ac;
    setStreaming(true); setStreamErr(null);
    try {
      try { const s0 = await getSession(sid); if (sessionIdRef.current === sid) setItems(s0.transcript as unknown as Transcript3Item[]); } catch { /* ignore */ }
      await streamEvents(sid, (ev) => applyEvent(ev, sid), ac.signal);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) setStreamErr(explainError(e));
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
      setStreaming(false);
      const s = await settle(sid);
      if (!ac.signal.aborted && s?.running && s.run_status === "running" && sessionIdRef.current === sid) {
        await new Promise((r) => setTimeout(r, 1000));
        if (!abortRef.current && sessionIdRef.current === sid) void attach(sid);
      }
    }
  }, [applyEvent, settle]);

  const load = useCallback(async (id: string) => {
    abortRef.current?.abort(); abortRef.current = null;
    sessionIdRef.current = id;
    setStreaming(false);                    // the chat we left may have been mid-turn; this one is not ours yet
    const s = await getSession(id);
    if (sessionIdRef.current !== id) return;
    setSessionId(id); setItems(s.transcript as unknown as Transcript3Item[]); setStreamErr(null); setLastSpeed(null);
    hydrate(s);
    if (s.running) void attach(id);
  }, [attach, hydrate]);

  const create = useMutation({
    mutationFn: () => createSession3(),
    // Switch to the new chat first; refresh the list in the background. Waiting for the list
    // (seconds on a busy server) made "+" look dead, and every extra press made another chat.
    onSuccess: async (s) => { void qc.invalidateQueries({ queryKey: ["agent3/sessions"] }); await load(s.id); },
    onError: (e) => setStreamErr(explainError(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteSession3(id),
    onSuccess: async () => {
      abortRef.current?.abort(); abortRef.current = null; sessionIdRef.current = null;
      setSessionId(null); setItems([]); setRunStatus("idle"); setPendingCallId(null);
      await qc.invalidateQueries({ queryKey: ["agent3/sessions"] });
    },
  });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items, streaming]);
  useEffect(() => {
    const d = status.data?.defaults;
    if (!d || sessionId) return;
    if (d.approval_mode === "bypass" || d.approval_mode === "ask") setMode(d.approval_mode);
    if (d.reasoning_effort === "xhigh" || d.reasoning_effort === "medium" || d.reasoning_effort === "low") setEffort(d.reasoning_effort);
  }, [status.data, sessionId]);

  const busy = runStatus === "running" || runStatus === "awaiting_approval";
  const needsAnswer = runStatus === "needs_input";
  const canSteer = runStatus === "running";

  /* ── attachments ── */
  const addAttachment = useCallback(async (src: string | Blob, source: string) => {
    if (attachments.length >= MAX_IMAGES) { setStreamErr(`At most ${MAX_IMAGES} images per message.`); return; }
    try {
      const f = await fitImage(src);
      setAttachments((a) => a.length >= MAX_IMAGES ? a : [...a, { id: Math.random().toString(36).slice(2), dataUrl: f.dataUrl, w: f.w, h: f.h, source }]);
    } catch (e) { setStreamErr(explainError(e)); }
  }, [attachments.length]);

  const onPaste = useCallback((e: React.ClipboardEvent) => {
    const files = [...e.clipboardData.items].filter((i) => i.type.startsWith("image/")).map((i) => i.getAsFile()).filter(Boolean) as File[];
    if (!files.length) return;
    e.preventDefault();
    for (const f of files.slice(0, MAX_IMAGES)) void addAttachment(f, "pasted");
  }, [addAttachment]);

  /* ── send / steer / approve / stop / compact ── */
  const send = useCallback(async (text: string) => {
    const imgs = attachments.map((a) => a.dataUrl);
    if (!text.trim() && !imgs.length) return;
    if (canSteer && sessionIdRef.current) {
      if (imgs.length) { setStreamErr("Images can be sent when the agent has finished this turn (or Stop it). Text alone is steered into the running turn."); return; }
      const sid0 = sessionIdRef.current;
      setDraft(""); setQueued((q) => [...q, text]);
      try { await steerRun3(sid0, text, turnId); }
      catch (e) { setQueued((q) => q.filter((x) => x !== text)); setStreamErr(explainError(e)); }
      return;
    }
    if (streaming || busy) return;
    if (text.trim() === "/compact" && sessionId) {
      setDraft(""); setStreaming(true);
      const ac = new AbortController(); abortRef.current = ac;
      try { await streamCompact(sessionId, (ev) => applyEvent(ev, sessionId), ac.signal); }
      catch (e) { setStreamErr(explainError(e)); }
      finally { if (abortRef.current === ac) abortRef.current = null; setStreaming(false); await settle(sessionId); }
      return;
    }
    let sid = sessionId;
    if (!sid) {
      const s = await createSession3();
      sid = s.id; sessionIdRef.current = sid; setSessionId(sid);
      void qc.invalidateQueries({ queryKey: ["agent3/sessions"] });
    }
    const sidNow = sid;
    setDraft(""); setAttachments([]); setStreamErr(null);
    setItems((cur) => [...cur, { type: "user", text, at: new Date().toISOString(), local_images: imgs }]);
    setStreaming(true); setRunStatus("running");
    const ac = new AbortController(); abortRef.current = ac;
    try {
      await streamMessage3(sidNow, text, imgs, (ev) => applyEvent(ev, sidNow), ac.signal, { approval_mode: mode, reasoning_effort: effort });
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) {
        setStreamErr(explainError(e));
        // the server refused the message (e.g. a bad image) — give the operator their draft back
        setDraft(text);
        setAttachments(imgs.map((d, i) => ({ id: `r${i}`, dataUrl: d, w: 0, h: 0, source: "restored" })));
      }
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
      setStreaming(false);
      const s = await settle(sidNow);
      if (!ac.signal.aborted && s?.running && s.run_status === "running" && sessionIdRef.current === sidNow) void attach(sidNow);
    }
  }, [attachments, canSteer, turnId, streaming, busy, sessionId, qc, applyEvent, settle, attach, mode, effort]);

  const decide = useCallback(async (callId: string, ok: boolean) => {
    const sid = sessionIdRef.current;
    if (!sid || streaming) return;
    setStreamErr(null); setPendingCallId(null); setRunStatus("running"); setStreaming(true);
    const ac = new AbortController(); abortRef.current = ac;
    try { await streamApprove(sid, callId, ok, (ev) => applyEvent(ev, sid), ac.signal); }
    catch (e) { if (!(e instanceof DOMException && e.name === "AbortError")) setStreamErr(explainError(e)); }
    finally {
      if (abortRef.current === ac) abortRef.current = null;
      setStreaming(false);
      const s = await settle(sid);
      if (!ac.signal.aborted && s?.running && s.run_status === "running" && sessionIdRef.current === sid) void attach(sid);
    }
  }, [streaming, applyEvent, settle, attach]);

  const stopRun = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid || !confirm("Stop the running turn? Any tool call still pending will be recorded as NOT EXECUTED.")) return;
    try { await cancelRun(sid); } catch (e) { setStreamErr(explainError(e)); }
    abortRef.current?.abort(); abortRef.current = null;
    await settle(sid);
  }, [settle]);

  const changeMode = (m: ApprovalMode) => { setMode(m); if (sessionId) void patchSession(sessionId, { approval_mode: m }).catch((e) => setStreamErr(explainError(e))); };
  const changeEffort = (e: ReasoningEffort) => { setEffort(e); if (sessionId) void patchSession(sessionId, { reasoning_effort: e }).catch((x) => setStreamErr(explainError(x))); };

  const mySessions = sessions.data ?? [];

  return (
    <div className="flex flex-col gap-2 animate-fade-up h-[calc(100vh-7.5rem)]">
      <header className="flex items-end justify-between flex-wrap gap-2 pb-2 border-b shrink-0" style={{ borderColor: "var(--rule)" }}>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">Operations · Agent 3</div>
          <h1 className="font-display text-[24px] leading-tight flex items-center gap-2">
            <Bot size={20} /> Operator agent 3
            <span className="np-chip font-mono text-[10px]" style={{ background: "var(--accent-soft)", color: "var(--accent)", borderColor: "transparent" }}>
              live browser · screenshots
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-3 font-mono text-[11px]">
          {status.data && (
            <span className="np-chip" style={{ background: configured ? "var(--bg-inset)" : "var(--accent-soft)", color: configured ? "var(--fg-muted)" : "var(--accent)", borderColor: "transparent" }}>
              {configured ? `${status.data.settings.provider} · ${status.data.settings.model}` : "no model endpoint configured"}
            </span>
          )}
          <button type="button" className="np-btn-ghost px-2 py-1 border flex items-center gap-1" style={{ borderColor: "var(--rule)" }}
                  onClick={() => setShowBrowser((v) => !v)} title={showBrowser ? "Hide the browser — chat gets the full width" : "Show the live browser"}>
            {showBrowser ? <PanelLeftClose size={13} /> : <PanelLeftOpen size={13} />} {showBrowser ? "Hide browser" : "Show browser"}
          </button>
        </div>
      </header>

      <div className={`grid gap-3 flex-1 min-h-0 min-w-0 ${showBrowser ? "grid-cols-[200px,minmax(0,1.55fr),minmax(380px,1fr)]" : "grid-cols-[200px,minmax(0,1fr)]"}`}>
        {/* ── sessions ── */}
        <aside className="np-card flex flex-col min-h-0">
          <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--rule)" }}>
            <span className="font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]">Sessions</span>
            <button className="np-btn-ghost p-1" onClick={() => create.mutate()} disabled={create.isPending} title="New session"><Plus size={13} /></button>
          </div>
          <ul className="overflow-y-auto flex-1">
            {mySessions.map((s) => (
              <li key={s.id}>
                <button onClick={() => void load(s.id)}
                        className={`w-full text-left px-3 py-2 border-b hover:bg-[var(--bg-inset)] ${s.id === sessionId ? "bg-[var(--bg-inset)]" : ""}`}
                        style={{ borderColor: "var(--rule)" }}>
                  <div className="font-sans text-[12px] truncate">{s.title}</div>
                  <div className="font-mono text-[9px] text-[var(--fg-faint)]">
                    {s.turns} turns · {s.tool_calls} tools · {timeSince(s.updated_at)}
                    {s.run_status === "running" && <span className="text-[var(--accent)]"> · running</span>}
                    {s.run_status === "awaiting_approval" && <span className="text-[var(--accent)]"> · needs approval</span>}
                  </div>
                </button>
              </li>
            ))}
            {sessions.data && mySessions.length === 0 && <li className="px-3 py-4 font-sans text-[12px] text-[var(--fg-muted)]">No Agent 3 sessions yet.</li>}
          </ul>
        </aside>

        {/* ── live browser ── */}
        {showBrowser && (
          <LiveBrowserPanel
            onShot={(dataUrl) => void addAttachment(dataUrl, "screenshot")}
            canAttach={attachments.length < MAX_IMAGES}
            onError={setStreamErr}
          />
        )}

        {/* ── chat ── */}
        <section className="np-card flex flex-col min-h-0 min-w-0">
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 flex flex-col gap-2">
            {items.length === 0 && (
              <div className="my-auto text-center px-2">
                <div className="font-display text-[17px] mb-1">Show the agent what you see</div>
                <div className="font-sans text-[12px] text-[var(--fg-muted)] mb-3">
                  Open a publisher in the live browser, take a screenshot, mark it up, and explain. The agent can drive the
                  same browser and save the session once you are logged in.
                </div>
                <div className="flex flex-col gap-1 items-stretch">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => setDraft(s)} className="np-btn-ghost font-sans text-[12px] text-left px-3 py-1.5 border" style={{ borderColor: "var(--rule)" }}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {items.map((it, i) => {
              if (it.type === "user") return <UserRow3 key={i} it={it} sessionId={sessionId} onOpen={setLightbox} />;
              if (it.type === "image") return <ToolImageRow key={i} it={it} onOpen={setLightbox} />;
              return (
                <TranscriptRow key={i} it={it as TranscriptItem} live={streaming && i === items.length - 1} pendingCallId={pendingCallId}
                               onDecide={(c, ok) => void decide(c, ok)} onAnswer={(t) => void send(t)} answerable={needsAnswer && i === items.length - 1} />
              );
            })}
            {(streaming || busy || needsAnswer) && (
              <div className="font-mono text-[10px] text-[var(--fg-faint)] flex items-center gap-2">
                <Spinner label="" />
                {runStatus === "awaiting_approval" ? "paused — waiting for your approval above"
                  : needsAnswer ? "waiting for your answer above"
                  : streaming ? (lastSpeed != null ? `working… last reply ${lastSpeed} tok/s` : "working…") : "running on the server — this tab is not attached"}
                {!streaming && runStatus === "running" && sessionId && <button type="button" className="underline" onClick={() => void attach(sessionId)}>re-attach</button>}
              </div>
            )}
            {streamErr && <div className="np-error font-mono text-[11px] flex items-start gap-2"><span className="flex-1">{streamErr}</span><button type="button" onClick={() => setStreamErr(null)}><XIcon size={11} /></button></div>}
            <div ref={bottomRef} />
          </div>

          <div className="border-t px-2 pt-1.5 flex items-center gap-2 flex-wrap font-mono text-[10px]" style={{ borderColor: "var(--rule)" }}>
            <div className="flex border" style={{ borderColor: "var(--rule)" }}>
              {(["bypass", "ask"] as const).map((m) => (
                <button key={m} type="button" onClick={() => changeMode(m)} className="px-2 py-0.5"
                        title={m === "bypass" ? "exec/write tools run without asking (capture still always asks)" : "pause and ask before every exec/write tool"}
                        style={{ background: mode === m ? "var(--accent-soft)" : "transparent", color: mode === m ? "var(--accent)" : "var(--fg-muted)" }}>
                  {m === "bypass" ? "Bypass" : "Needs approval"}
                </button>
              ))}
            </div>
            <select value={effort} onChange={(e) => changeEffort(e.target.value as ReasoningEffort)} className="np-input w-auto py-0.5 px-1 font-mono text-[10px]" title="Reasoning effort">
              <option value="low">low</option><option value="medium">medium</option><option value="xhigh">xhigh</option>
            </select>
            {ledger.length > 0 && (
              <span className="text-[var(--fg-faint)] flex items-center gap-1" title={ledger.map((l) => `[${l.status}] ${l.text}`).join("\n")}>
                <ListChecks size={11} /> {ledger.filter((l) => l.status === "done").length}/{ledger.length}
              </span>
            )}
            <span className="ml-auto text-[var(--fg-faint)]">history {historyChars.toLocaleString()} chars</span>
            {busy && <button type="button" className="np-btn-ghost px-2 py-0.5 border" style={{ borderColor: "var(--accent)", color: "var(--accent)" }} onClick={() => void stopRun()}>Stop run</button>}
          </div>
          {queued.length > 0 && (
            <div className="px-2 pt-1 flex flex-wrap items-center gap-1 font-mono text-[10px]">
              <span className="text-[var(--fg-faint)] uppercase tracking-folio">queued</span>
              {queued.map((q, i) => <span key={i} className="np-chip" style={{ background: "var(--bg-inset)", borderColor: "var(--rule)" }}>{q.length > 40 ? q.slice(0, 40) + "…" : q}</span>)}
              <button type="button" className="np-btn-ghost px-1" title="Take back everything still queued"
                      onClick={() => { const sid = sessionIdRef.current; if (sid) void clearSteers(sid).then(() => setQueued([])).catch(() => undefined); }}><XIcon size={11} /></button>
            </div>
          )}

          {attachments.length > 0 && (
            <div className="px-2 pt-2 flex gap-2 flex-wrap" aria-label="Attached images">
              {attachments.map((a) => (
                <div key={a.id} className="relative group border" style={{ borderColor: a.marked ? "var(--accent)" : "var(--rule)" }}>
                  <button type="button" onClick={() => setAnnotating(a)} title="Click to mark it up (box / arrow / pen)" className="block">
                    <img src={a.dataUrl} alt={a.source} className="h-[64px] w-auto max-w-[120px] object-cover block" />
                    <span className="absolute bottom-0 left-0 right-0 font-mono text-[9px] px-1 flex items-center gap-1"
                          style={{ background: "rgba(0,0,0,.6)", color: "#fff" }}>
                      <Pencil size={9} /> {a.marked ? "marked" : "mark up"}
                    </span>
                  </button>
                  <button type="button" aria-label="Remove image" onClick={() => setAttachments((x) => x.filter((y) => y.id !== a.id))}
                          className="absolute -top-2 -right-2 rounded-full p-0.5" style={{ background: "var(--fg)", color: "var(--bg)" }}><XIcon size={10} /></button>
                </div>
              ))}
              <span className="font-mono text-[9px] text-[var(--fg-faint)] self-end">{attachments.length}/{MAX_IMAGES}</span>
            </div>
          )}

          <form className="p-2 flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); void send(draft); }}>
            <div className="flex flex-col gap-1">
              <button type="button" className="np-btn-ghost p-2 border" style={{ borderColor: "var(--rule)" }} title="Attach an image (or paste one into the box)"
                      disabled={attachments.length >= MAX_IMAGES} onClick={() => fileRef.current?.click()}><ImagePlus size={14} /></button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden
                     onChange={(e) => { for (const f of [...(e.target.files ?? [])].slice(0, MAX_IMAGES)) void addAttachment(f, f.name); e.target.value = ""; }} />
            </div>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} onPaste={onPaste} rows={3}
                      placeholder={runStatus === "awaiting_approval" ? "Waiting for your approval above…"
                        : needsAnswer ? "The agent asked you a question — answer here."
                        : canSteer ? "The agent is working — type to steer it (text only)."
                        : "Explain what you see; attach or paste screenshots…  (/compact to shrink)"}
                      disabled={(streaming || busy) && !canSteer && !needsAnswer}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(draft); } }}
                      className="np-input flex-1 font-sans text-[13px] resize-none" />
            {streaming && !canSteer ? (
              <button type="button" className="np-btn-ghost p-2" onClick={() => abortRef.current?.abort()} title="Stop watching (the run continues on the server)"><Square size={14} /></button>
            ) : (
              <button type="submit" className="np-btn p-2" title={canSteer ? "Steer the running turn" : "Send"}
                      disabled={(!draft.trim() && !attachments.length) || !configured || (busy && !canSteer && !needsAnswer)}><Send size={14} /></button>
            )}
            {sessionId && !streaming && !busy && (
              <button type="button" className="np-btn-ghost p-2" title="Delete session" onClick={() => { if (confirm("Delete this session and its images?")) remove.mutate(sessionId); }}><Trash2 size={14} /></button>
            )}
          </form>
        </section>
      </div>

      {annotating && (
        <ImageAnnotator src={annotating.dataUrl} onCancel={() => setAnnotating(null)}
                        onDone={(url) => { setAttachments((a) => a.map((x) => x.id === annotating.id ? { ...x, dataUrl: url, marked: true } : x)); setAnnotating(null); }} />
      )}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 cursor-zoom-out" style={{ background: "rgba(10,10,10,.82)" }} onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-w-full max-h-full" style={{ boxShadow: "0 6px 30px rgba(0,0,0,.5)" }} />
        </div>
      )}
    </div>
  );
}

/* ── chat rows with images ─────────────────────────────────────────── */
function UserRow3({ it, sessionId, onOpen }: { it: Transcript3Item; sessionId: string | null; onOpen: (src: string) => void }) {
  return (
    <div className="self-end max-w-[90%] px-3 py-2 font-sans text-[13px]" style={{ background: "var(--bg-inset)", borderLeft: "3px solid var(--primary)" }}>
      {it.text && <div className="whitespace-pre-wrap break-words">{it.text}</div>}
      {(it.local_images?.length || it.images?.length) ? (
        <div className="flex gap-1.5 mt-1.5 flex-wrap">
          {it.local_images?.length
            ? it.local_images.map((u, i) => <Thumb key={i} src={u} onOpen={onOpen} />)
            : it.images!.map((im) => <AuthThumb key={im.name} sessionId={sessionId} name={im.name} onOpen={onOpen} />)}
        </div>
      ) : null}
    </div>
  );
}

function Thumb({ src, onOpen }: { src: string; onOpen: (s: string) => void }) {
  return (
    <button type="button" onClick={() => onOpen(src)} title="Open full size" className="border" style={{ borderColor: "var(--rule)" }}>
      <img src={src} alt="attached" className="h-[90px] w-auto max-w-[200px] object-cover block" />
    </button>
  );
}

/** <img> cannot send the bearer token, so attached images are fetched as blobs. */
function AuthThumb({ sessionId, name, onOpen }: { sessionId: string | null; name: string; onOpen: (s: string) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!sessionId) return;
    let alive = true, made: string | null = null;
    fetchSessionImage(sessionId, name)
      .then((b) => { made = URL.createObjectURL(b); if (alive) setUrl(made); })
      .catch(() => alive && setFailed(true));
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [sessionId, name]);
  if (failed) return <span className="font-mono text-[10px] text-[var(--fg-faint)]">[image {name} unavailable]</span>;
  if (!url) return <span className="h-[90px] w-[120px] inline-block" style={{ background: "var(--rule)" }} />;
  return <Thumb src={url} onOpen={onOpen} />;
}

function ToolImageRow({ it, onOpen }: { it: Transcript3Item; onOpen: (s: string) => void }) {
  if (!it.data_url) return null;
  return (
    <div className="ml-3 font-mono text-[10px] text-[var(--fg-faint)]">
      <div className="mb-1">↳ the agent looked at this ({it.name})</div>
      <button type="button" onClick={() => onOpen(it.data_url!)} className="border" style={{ borderColor: "var(--rule)" }} title="Open full size">
        <img src={it.data_url} alt={it.name ?? "screenshot"} className="max-h-[160px] w-auto max-w-full block" />
      </button>
    </div>
  );
}

/* ── the live browser ─────────────────────────────────────────────── */
function LiveBrowserPanel({ onShot, canAttach, onError }: { onShot: (dataUrl: string) => void; canAttach: boolean; onError: (e: string | null) => void }) {
  const qc = useQueryClient();
  const live = useQuery({ queryKey: ["agent3/browser"], queryFn: fetchLiveBrowser, refetchInterval: 4000, retry: 1 });
  const b: LiveBrowser | null = live.data?.active ?? null;
  const [channelId, setChannelId] = useState("");
  const [mode, setMode] = useState<"navigate" | "login">("navigate");
  const [startUrl, setStartUrl] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [frameKey, setFrameKey] = useState(0);
  const [last, setLast] = useState<string | null>(null);

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label); onError(null);
    try { const r = await fn(); await qc.invalidateQueries({ queryKey: ["agent3/browser"] }); return r; }
    catch (e) { onError(explainError(e)); return null; }
    finally { setBusy(null); }
  };

  const shot = async () => {
    const r = await act("screenshot", () => liveScreenshot(b?.channel_id)) as { data_url?: string; url?: string } | null;
    if (r?.data_url) { onShot(r.data_url); setLast(r.url ?? null); }
  };

  const phase = b?.status ?? "";
  const novncUrl = useMemo(() => b?.novnc_url ?? null, [b?.novnc_url]);
  const vncPass = useVncPass(!!novncUrl);     // nginx refuses /vnc/ without this login-issued cookie
  const novnc = vncPass.ready ? novncUrl : null;

  return (
    <section className="np-card flex flex-col min-h-0 min-w-0">
      <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--rule)" }}>
        <Globe size={13} className="text-[var(--fg-faint)]" />
        <span className="font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]">Live browser</span>
        {b ? (
          <>
            <span className="np-chip font-mono text-[10px]" style={{ background: "var(--bg-inset)", borderColor: "transparent", color: PHASE_TONE[phase] ?? "var(--fg-muted)" }}>
              ● {phase.replace(/_/g, " ")}
            </span>
            <span className="font-mono text-[10px] text-[var(--fg-muted)] truncate">
              ch {b.channel_id}{b.channel_name ? ` · ${b.channel_name}` : ""} · {b.mode} · {b.owner_text}
              {typeof b.captured === "number" ? ` · ${b.captured} requests` : ""}
            </span>
          </>
        ) : (
          <span className="font-mono text-[10px] text-[var(--fg-faint)]">{live.isLoading ? "checking the crawler host…" : live.error ? "crawler host unreachable" : "no browser open"}</span>
        )}
        <button type="button" className="np-btn-ghost p-1 ml-auto" title="Refresh status" onClick={() => void live.refetch()}><RefreshCw size={12} /></button>
      </div>

      {b && (
        <div className="px-3 py-1.5 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--rule)" }}>
          <button type="button" className="np-btn px-2 py-1 flex items-center gap-1 font-mono text-[11px]" disabled={!b.drivable || !!busy || !canAttach}
                  onClick={() => void shot()} title={canAttach ? "Grab the current view into the chat (then click it to mark it up)" : `At most ${MAX_IMAGES} images per message`}>
            <Camera size={13} /> {busy === "screenshot" ? "capturing…" : "Screenshot"}
          </button>
          {phase === "waiting_for_login" && (
            <button type="button" className="np-btn-ghost px-2 py-1 border flex items-center gap-1 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }} disabled={!!busy}
                    onClick={() => void act("loggedin", () => liveSignal("loggedin", b.channel_id))} title="You have signed in — the browser visits the e-paper host so the login hand-off completes">
              <Check size={13} /> I&apos;m logged in
            </button>
          )}
          <button type="button" className="np-btn-ghost px-2 py-1 border flex items-center gap-1 font-mono text-[11px]" style={{ borderColor: "var(--accent)", color: "var(--accent)" }}
                  disabled={!b.drivable || !!busy}
                  onClick={() => { if (confirm(`Capture the session for channel ${b.channel_id}?\n\nThis saves cookies, storage and the network trace and REPLACES the stored session the crawler uses. The browser closes afterwards.`)) void act("capture", () => liveSignal("done", b.channel_id)); }}>
            <Save size={13} /> {busy === "capture" ? "capturing session…" : "Capture session"}
          </button>
          <button type="button" className="np-btn-ghost px-2 py-1 border flex items-center gap-1 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }} disabled={!!busy}
                  onClick={() => { if (confirm(`Close the browser for channel ${b.channel_id} without saving?${(b.owner_text ?? "").includes("Sessions page") ? "\n\nIt was opened from the Sessions page — someone may be using it." : ""}`)) void act("cancel", () => liveSignal("cancel", b.channel_id)); }}>
            <Power size={13} /> Close
          </button>
          {last && <span className="font-mono text-[10px] text-[var(--fg-faint)] truncate max-w-[40%]" title={last}>last shot: {last}</span>}
          {novnc && <a href={novnc} target="_blank" rel="noreferrer" className="font-mono text-[10px] underline text-[var(--fg-faint)] ml-auto flex items-center gap-1"><ExternalLink size={10} /> new tab</a>}
          {novnc && <button type="button" className="np-btn-ghost p-1" title="Reconnect the viewer" onClick={() => setFrameKey((k) => k + 1)}><RefreshCw size={11} /></button>}
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col" style={{ background: b && novnc && b.drivable ? "#000" : "transparent" }}>
        {b && novnc && (b.drivable || phase === "browser_ready") ? (
          <iframe key={frameKey} title="Live browser" src={novnc} className="w-full flex-1 min-h-[420px]" style={{ border: "none" }} allow="clipboard-read; clipboard-write" />
        ) : b ? (
          <div className="m-auto text-center font-sans text-[12px] text-[var(--fg-muted)] p-6 flex flex-col items-center gap-2">
            <Spinner label="" />
            {phase === "capturing" ? "Capturing the full session — cookies, storage and the network trace…" : b.message ?? "Starting the remote browser (≈15 s)…"}
          </div>
        ) : (
          <form className="m-auto w-full max-w-[460px] p-4 flex flex-col gap-2"
                onSubmit={(e) => { e.preventDefault(); const c = Number(channelId); if (c > 0) void act("open", () => openLiveBrowser(c, mode, startUrl)); }}>
            <div className="font-display text-[16px]">Open the live browser</div>
            <div className="font-sans text-[12px] text-[var(--fg-muted)]">
              The crawler host&apos;s capture Chrome, for one channel. <b>Navigate</b> loads the channel&apos;s stored session;
              <b> Login</b> starts with an empty cookie jar. Whatever you capture is saved for that channel.
            </div>
            <label className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">Channel id
              <input value={channelId} onChange={(e) => setChannelId(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="e.g. 41"
                     className="np-input w-full font-mono text-[12px] mt-0.5" />
            </label>
            <div className="flex border self-start font-mono text-[11px]" style={{ borderColor: "var(--rule)" }}>
              {(["navigate", "login"] as const).map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)} className="px-3 py-1"
                        style={{ background: mode === m ? "var(--accent-soft)" : "transparent", color: mode === m ? "var(--accent)" : "var(--fg-muted)" }}>{m}</button>
              ))}
            </div>
            <label className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">Start URL (optional)
              <input value={startUrl} onChange={(e) => setStartUrl(e.target.value)} placeholder="https://epaper.example.com/" className="np-input w-full font-mono text-[12px] mt-0.5" />
            </label>
            <button type="submit" className="np-btn px-3 py-1.5 self-start flex items-center gap-1 font-mono text-[11px]" disabled={!Number(channelId) || !!busy}>
              <Globe size={13} /> {busy === "open" ? "starting…" : "Open browser"}
            </button>
            <div className="font-mono text-[10px] text-[var(--fg-faint)]">Only one capture browser can run at a time (shared with the Sessions page). The agent can also open it.</div>
          </form>
        )}
      </div>
    </section>
  );
}
